import { FORM_ERROR } from "final-form";
import { ReplyIcon } from "lucide-react";
import { useField, useForm } from "react-final-form-hooks";
import { FormattedMessage, useIntl } from "react-intl";

import classNames from "components/ui/classNames";

import restartForm from "lib/utils/form/restartForm";

import type { ISelfieQuery } from "../../../lib/queries/__generated__/SelfieQuery.generated";
import checkCanSubmit from "../../../lib/utils/form/checkCanSubmit";
import validateFormValuesAgainstSchema from "../../../lib/utils/form/validateFormValuesAgainstSchema";
import Avatar from "../../core/Avatar";
import CommentInputTextArea from "../CommentInputTextArea";

import commentInputFormDataShape from "./shapes/commentInputFormDataShape";
import translateServerErrorIntoFriendlyMessage from "./translateServerErrorIntoFriendlyMessage";

interface IReplyCommentInputFormProps {
  viewer: NonNullable<ISelfieQuery["selfie"]>;
  bodyAsPlainText?: string;
  hideAvatar?: boolean;
  autoFocus?: boolean;
  hintAboutSupportMessagesBeingDeleted?: boolean;
  onCancel?: () => void;
  onSubmit: (bodyAsPlainText: string) => Promise<any>;
}

export default function ReplyCommentInputForm({
  viewer,
  bodyAsPlainText = "",
  hideAvatar = false,
  autoFocus = false,
  onSubmit,
}: IReplyCommentInputFormProps) {
  const intl = useIntl();
  const { form, handleSubmit, submitting, submitError, ...props } = useForm<{
    bodyAsPlainText: string;
  }>({
    onSubmit: async ({ bodyAsPlainText }, form) => {
      try {
        await onSubmit(bodyAsPlainText);

        setTimeout(() => {
          restartForm(form);
        }, 1);
      } catch (err) {
        if (err instanceof Error) {
          return {
            [FORM_ERROR]: translateServerErrorIntoFriendlyMessage(err),
          };
        }
        return {
          [FORM_ERROR]: "Erro desconhecido",
        };
      }

      return null;
    },
    validate: validateFormValuesAgainstSchema(commentInputFormDataShape),
    initialValues: {
      bodyAsPlainText,
    },
  });

  const bodyAsPlainTextField = useField("bodyAsPlainText", form);
  const canSubmit = submitting ? false : checkCanSubmit(props);

  return (
    <div className="space-y-2">
      <div
        className={classNames("grid grid-cols-1 gap-x-4 sm:grid-cols-[42px_1fr]", {
          "grid-cols-1 sm:grid-cols-1": hideAvatar,
        })}
      >
        {!hideAvatar && (
          <Avatar
            src={viewer.pictureUrl}
            hashValue={viewer._id}
            alt={viewer.fullName}
            size={42}
            className="hidden sm:inline-flex"
          />
        )}

        <form onSubmit={handleSubmit} className="flex flex-wrap items-center gap-x-2 gap-y-4 sm:flex-nowrap">
          <CommentInputTextArea
            {...bodyAsPlainTextField.input}
            autoFocus={autoFocus}
            placeholder={intl.formatMessage({
              id: "SQv765",
              defaultMessage: "Escreva uma resposta aqui...",
            })}
            onKeyDown={(event) => {
              if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                handleSubmit();
              }
            }}
          />

          <button
            type="submit"
            disabled={!canSubmit}
            className={classNames(
              "w-full rounded-xl border border-slate-200 bg-slate-50/60 p-2 text-slate-600 sm:w-auto",
              "dark:border-slate-950 dark:bg-slate-700 dark:text-slate-300",
              "focus-visible:outline-none",
              "focus-visible:ring-1 focus-visible:ring-offset-1",
              "focus-visible:ring-gray-100 focus-visible:ring-offset-gray-300",
              "focus-visible:border-transparent focus-visible:bg-gray-50 focus-visible:text-gray-600",
              "dark:focus-visible:ring-slate-950 dark:focus-visible:ring-offset-transparent",
              "dark:bg-teal-600/20 dark:text-teal-200",
              "disabled:opacity-60",
              "dark:disabled:bg-slate-600/20 dark:disabled:text-slate-500",
            )}
          >
            <ReplyIcon strokeWidth={2} className="hidden sm:inline-flex" />
            <span className="inline-flex w-full justify-center text-sm font-medium sm:hidden">
              <FormattedMessage defaultMessage="Enviar resposta" id="f6/BJx" />
            </span>
          </button>
        </form>
      </div>
      {bodyAsPlainTextField.meta.active && bodyAsPlainTextField.meta.touched && bodyAsPlainTextField.meta.error && (
        <p className="py-1 text-sm tracking-wide text-red-700 dark:text-red-300">{bodyAsPlainTextField.meta.error}</p>
      )}

      {submitError && (
        <div className="grid grid-cols-[42px_1fr] gap-x-4">
          <div />
          <p className="text-sm tracking-wide text-red-700 dark:text-red-300">{submitError}</p>
        </div>
      )}
    </div>
  );
}
